/**
 * EltJobExecutionDetailsModal Component
 *
 * Modal for viewing job execution details with option to re-run failed transformations.
 */

import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { Spinner } from "../../../components/ui/Spinner";
import { Alert } from "../../../components/ui/Alert";
import { EltJobExecutionStatusBadge } from "./EltJobExecutionStatusBadge";
import { PhaseStatusTimeline } from "./PhaseStatusBadge";
import { TriggerBadge } from "./TriggerBadge";
import { ExecutionLogsViewer } from "./ExecutionLogsViewer";
import { TransformationStepResult } from "./TransformationStepResult";
import { LoadStepResult } from "./LoadStepResult";
import { useEltJobExecutionDetails } from "../hooks/useEltJobExecutionDetails";
import { jobExecutionService } from "../../../api/job-execution.service";
import { getErrorMessage } from "../../../lib/utils/errors";

type TabType = "overview" | "logs";

interface EltJobExecutionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  jobExecutionId: string;
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{children}</dd>
  </div>
);

export const EltJobExecutionDetailsModal: React.FC<EltJobExecutionDetailsModalProps> = ({ isOpen, onClose, tenantId, jobExecutionId }) => {
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const queryClient = useQueryClient();
  const { data: execution, isLoading, error } = useEltJobExecutionDetails(tenantId, jobExecutionId);

  // Mutation for re-running transformation
  const rerunMutation = useMutation({
    mutationFn: () => jobExecutionService.reRunTransformation(tenantId, jobExecutionId),
    onSuccess: () => {
      // Invalidate queries to refresh the execution data
      queryClient.invalidateQueries({ queryKey: ["job-execution", tenantId, jobExecutionId] });
      queryClient.invalidateQueries({ queryKey: ["job-executions", tenantId] });
      setShowRerunConfirm(false);
    },
  });

  // Check if transformation can be re-run
  // Allows rerun for any execution with successful load status
  const canRerunTransformation = execution && execution.loadStatus === "succeeded";

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (): string => {
    if (!execution?.startedAt) return "-";
    const start = new Date(execution.startedAt).getTime();
    const end = execution.completedAt ? new Date(execution.completedAt).getTime() : Date.now();
    const seconds = (end - start) / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)} seconds`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>Job Execution Details</Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        )}

        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {execution && (
          <div className="space-y-6">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab("overview")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "overview"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab("logs")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "logs"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Logs
                </button>
              </nav>
            </div>

            {/* Overview Tab */}
            {activeTab === "overview" && (
              <>
                {/* Overview */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Overview</h3>
                  <dl className="border-t border-gray-200">
                    <DetailRow label="Execution Code">
                      <span className="font-mono text-sm">{execution.jobExecutionCode}</span>
                    </DetailRow>
                    <DetailRow label="Overall Status">
                      <EltJobExecutionStatusBadge status={execution.status} />
                    </DetailRow>
                    <DetailRow label="Trigger">
                      <TriggerBadge trigger={execution.triggeredBy} />
                    </DetailRow>
                  </dl>
                </div>

                {/* Phase Progress */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Phase Progress</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <PhaseStatusTimeline
                      extractionStatus={execution.extractionStatus}
                      loadStatus={execution.loadStatus}
                      transformStatus={execution.transformStatus}
                      currentStep={execution.currentStep}
                    />
                  </div>
                </div>

                {/* Load Results */}
                {execution.queryLoadResults && execution.queryLoadResults.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Load Results</h3>
                    <div className="space-y-2">
                      {execution.queryLoadResults.map((result, index) => (
                        <LoadStepResult key={result.queryId} result={result} index={index} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Variable Overrides */}
                {execution.variableOverrides && Object.keys(execution.variableOverrides).length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Variable Overrides</h3>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <dl className="space-y-1">
                        {Object.entries(execution.variableOverrides).map(([key, value]) => (
                          <div key={key} className="flex gap-2 text-sm">
                            <dt className="font-mono text-gray-600">{key}:</dt>
                            <dd className="text-gray-900">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {execution.errorMessage && (
                  <div>
                    <h3 className="text-lg font-medium text-red-700 mb-3">Error</h3>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                      {execution.failedStep && (
                        <p className="text-sm text-red-600">
                          <span className="font-medium">Failed Step:</span> {execution.failedStep}
                        </p>
                      )}
                      <p className="text-sm text-red-700 whitespace-pre-wrap">{execution.errorMessage}</p>
                    </div>
                  </div>
                )}

                {/* Transformation Step Results */}
                {((execution.stagingStepResults && execution.stagingStepResults.length > 0) ||
                  (execution.productionStepResults && execution.productionStepResults.length > 0)) && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Transformation Steps</h3>

                    {/* Staging Steps */}
                    {execution.stagingStepResults && execution.stagingStepResults.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">Staging</span>
                          Phase
                        </h4>
                        <div className="space-y-2">
                          {execution.stagingStepResults.map((step, index) => (
                            <TransformationStepResult key={step.stepId || index} step={step} index={index} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Production Steps */}
                    {execution.productionStepResults && execution.productionStepResults.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Production</span>
                          Phase
                        </h4>
                        <div className="space-y-2">
                          {execution.productionStepResults.map((step, index) => (
                            <TransformationStepResult key={step.stepId || index} step={step} index={index} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Timing */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Timing</h3>
                  <dl className="border-t border-gray-200">
                    <DetailRow label="Triggered At">{formatDate(execution.triggeredAt)}</DetailRow>
                    <DetailRow label="Started At">{formatDate(execution.startedAt)}</DetailRow>
                    <DetailRow label="Completed At">{formatDate(execution.completedAt)}</DetailRow>
                    <DetailRow label="Duration">{formatDuration()}</DetailRow>
                  </dl>
                </div>

                {/* Run Transformation option - shown when load was successful */}
                {canRerunTransformation && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700 mb-2">
                      Data was loaded successfully. You can run the transformation phase again using the data in the landing tables.
                    </p>
                    <Button variant="primary" size="sm" onClick={() => setShowRerunConfirm(true)} disabled={rerunMutation.isPending}>
                      Run Transformation
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Logs Tab */}
            {activeTab === "logs" && <ExecutionLogsViewer tenantId={tenantId} jobExecutionId={jobExecutionId} />}

            {/* Run Transformation Confirmation Dialog */}
            {showRerunConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Run Transformation</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This will create a new execution to run all staging and production transformations using the data in the landing tables. The
                    original execution will be preserved.
                  </p>
                  {rerunMutation.error && (
                    <Alert variant="error" className="mb-4">
                      {getErrorMessage(rerunMutation.error)}
                    </Alert>
                  )}
                  <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={() => setShowRerunConfirm(false)} disabled={rerunMutation.isPending}>
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={() => rerunMutation.mutate()} disabled={rerunMutation.isPending}>
                      {rerunMutation.isPending ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Running...
                        </>
                      ) : (
                        "Confirm"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end pt-4 border-t">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
